import { useMemo, useRef, useState } from 'react'
import {
  EditCapacityTableDataType,
  EditCapacityTableProps,
} from './EditCapacity.types'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { GridRowId, GridCellParams } from '@mui/x-data-grid-premium'
import { Box } from '@mui/material'
import { weekSorter } from 'Common/utils/utils'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'
import { is } from 'date-fns/locale'

export const EditCapacityTable = ({
  updateTableData,
  data,
  getFilteredWeeks,
  isLoading,
}: EditCapacityTableProps) => {
  const timerRef = useRef<null | NodeJS.Timeout>(null)

  // Function to handle capacity adjustment updates
  const handleCapacityUpdate = (
    changedRows: Record<number, EditCapacityTableDataType>
  ) => {
    const updatedData = [...data]
    Object.entries(changedRows).forEach(([rowId, changedRow]) => {
      const rowIndex = parseInt(rowId)
      if (updatedData[rowIndex]) {
        updatedData[rowIndex] = changedRow
      }
    })
    updateTableData(updatedData)
    getFilteredWeeks(Object.values(changedRows))
  }

  // Process row function to handle capacity calculations
  const processRow = (
    newRow: EditCapacityTableDataType,
    oldRow: EditCapacityTableDataType
  ): EditCapacityTableDataType => {
    const { capacityAdjustment, defaultWorkCentreCapacity } = newRow
    const { capacityAdjustment: oldCapacityAdjustment } = oldRow

    if (capacityAdjustment !== oldCapacityAdjustment) {
      newRow.adjustedWorkCentreCapacity =
        capacityAdjustment + defaultWorkCentreCapacity
    }

    return newRow
  }

  const weeks = useMemo(
    () =>
      data
        ?.map((capacityRow: EditCapacityTableDataType) => {
          return capacityRow.productionDateKeyFormatted
        })
        .filter((value, index, self) => self.indexOf(value) === index) // Remove duplicates
        .sort((aValue, bValue) => weekSorter(aValue, bValue)),
    [data]
  )

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Week',
        field: 'productionDateKeyFormatted',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'Contrained horizon',
        field: 'isConstrainedHorizon',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Unconstrained hours',
        field: 'unconstrainedTotalProductionHours',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Constrained hours',
        field: 'totalProductionHours',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Constraining difference',
        field: 'constrainingDifferenceHours',
        type: 'number',
        flex: 1,
        cellClassName: (
          params: GridCellParams<EditCapacityTableDataType, number>
        ) => {
          if (Number(params.value) === 0) return ''
          if (Number(params?.value) < 0) return 'reddish'
          if (Number(params?.value) > 0) return 'yellowish'
          return ''
        },
      },
      {
        headerName: 'Default capacity',
        field: 'defaultWorkCentreCapacity',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Adjusted capacity',
        field: 'adjustedWorkCentreCapacity',
        type: 'number',
        flex: 1,
      },
      {
        headerName: 'Capacity adjustment',
        field: 'capacityAdjustment',
        type: 'number',
        flex: 1,
        editable: true,
      },
      {
        headerName: 'Capacity utilisation',
        field: 'capacityUtilisation',
        type: 'number',
        flex: 1,
        valueFormatter: (value: number) =>
          value !== null ? `${value?.toFixed(2)}%` : '',
        cellClassName: (
          params: GridCellParams<EditCapacityTableDataType, number>
        ) => {
          if (Number(params.value) === 0) return ''
          if (Number(params?.value) > 100) return 'reddish'
          return ''
        },
      },
      {
        headerName: 'Spare capacity',
        field: 'spareCapacity',
        type: 'number',
        flex: 1,
        cellClassName: (
          params: GridCellParams<EditCapacityTableDataType, number>
        ) => {
          if (Number(params?.value) === 0) return ''
          if (Number(params?.value) < 0) return 'reddish'
          return ''
        },
      },
      {
        headerName: 'Cumulative Utilisation',
        field: 'cumulativeCapacityUtilisation',
        valueFormatter: (value: number) => {
          const val = value !== null ? `${value?.toFixed(2)}%` : ''
          return val
        },
        type: 'number',
        flex: 1,
      },
    ],
    [weeks]
  )

  return (
    <Box>
      <BaseTable
        data={data ?? []}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleCapacityUpdate}
        processRow={processRow}
        uploadData={() => {}} // No upload functionality needed
        isUploading={false}
        isSaving={false}
        checkBoxSelection={true}
        showTableToolBar={true}
        downloadFileName="EditCapacity"
        sx={{
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
          '& .MuiDataGrid-cell.reddish': {
            backgroundColor: '#FECACA !important',
            fontWeight: '600',
          },
          '& .MuiDataGrid-cell.yellowish': {
            backgroundColor: '#FDE68A !important',
            fontWeight: '600',
          },
        }}
        isCellEditable={(params) => params.field === 'capacityAdjustment'}
      />
    </Box>
  )
}
